import { Download, Eye, Loader2, Save } from "lucide-react";
import { money } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";
import { isStaff } from "@/lib/staffView";

const Line = ({ label, value, testId, strong, muted }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className={strong ? "text-sm font-semibold text-slate-200" : `text-sm ${muted ? "text-slate-500" : "text-slate-400"}`}>{label}</span>
    <span data-testid={testId} className={`font-mono-num ${strong ? "text-2xl font-bold text-amber-400" : muted ? "text-sm text-slate-500" : "text-base text-slate-200"}`}>{money(value)}</span>
  </div>
);

const Btn = ({ testId, onClick, disabled, busy, icon: Icon, children, primary }) => (
  <button data-testid={testId} onClick={onClick} disabled={disabled}
    className={`flex h-11 w-full items-center justify-center gap-2 rounded-full text-sm transition-colors disabled:opacity-60 ${primary ? "bg-amber-500 font-semibold text-slate-950 hover:bg-amber-400" : "border border-slate-700 font-medium text-slate-200 hover:border-amber-500/60 hover:text-amber-200"}`}>
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />} {children}
  </button>
);

export function TotalsPanel({ totals, multiplier, taxRate, upgradeLabel, count, warnings, onSave, onPdf, onPreview, busy, saved }) {
  const staff = isStaff(useAuth().user);
  return (
    <div className="sticky top-24 space-y-6 rounded-2xl border border-slate-800 bg-[#F2F2F7] p-6">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Totals</p>
        <p className="mt-1 text-xs text-slate-500" data-testid="totals-meta">{count} line{count === 1 ? "" : "s"}{staff ? "" : ` · multiplier ×${multiplier}`}</p>
      </div>
      <div className="space-y-2.5">
        {!staff && <Line label="Cost (internal)" value={totals.cost} testId="totals-cost-sum" muted />}
        <Line label="Suggested" value={totals.suggested} testId="totals-suggested-sum" muted />
        <div className="space-y-2.5 border-t border-slate-800 pt-3">
          <Line label="Subtotal" value={totals.subtotal} testId="totals-final-sum" />
          <Line label={upgradeLabel || "Upgrade / discount"} value={totals.upgrade} testId="totals-upgrade" />
          <Line label="Shipping & delivery" value={totals.shipping} testId="totals-shipping" />
          <Line label={`Tax (${Number(taxRate) || 0}%)`} value={totals.tax} testId="totals-tax" />
        </div>
        <div className="border-t border-slate-800 pt-4"><Line label="Project total" value={totals.project_total} testId="totals-project-total" strong /></div>
      </div>
      {warnings > 0 && (
        <p data-testid="totals-warning" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {warnings} line{warnings === 1 ? "" : "s"} priced at $0: pick a type or fill in the size.
        </p>
      )}
      <div className="space-y-2.5">
        <Btn testId="save-quote-button" onClick={onSave} disabled={!!busy} busy={busy === "save"} icon={Save} primary>{saved ? "Save changes" : "Save quote"}</Btn>
        <Btn testId="preview-estimate-button" onClick={onPreview} disabled={!!busy} busy={busy === "preview"} icon={Eye}>Preview estimate</Btn>
        <Btn testId="download-pdf-button" onClick={onPdf} disabled={!!busy} busy={busy === "pdf"} icon={Download}>Download PDF</Btn>
        <p className="text-center text-[11px] text-slate-500">The estimate shows final prices only. Never cost or multiplier.</p>
      </div>
    </div>
  );
}
